import {
  ExposureDirectionType,
  FootprintPolygonsType,
  LevelType,
  PositionType
} from 'data/types';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import {
  geometryOriginFeet,
  openingRunsWithOpenNeighborOverhang,
  openNeighborOpeningRuns
} from 'utils/open-neighbor-opening-runs';
import {
  OpenNeighborRunsType,
  OpenNeighborSpaceType
} from 'utils/open-neighbor-opening-runs-types';
import {
  getSpaceEdgeRunsFromFeet,
  runBoundsForEdge,
  spaceToFeetEdgeRunGeometry
} from 'utils/opening-layout';
import {
  EdgeRunType,
  OpeningEdgeType,
  OpeningRunBoundsType
} from 'utils/opening-position-types';
import { OpeningRunSpaceType } from 'utils/space-opening-run-types';
import { spaceWallEdges } from 'utils/space-wall-on-edge';
import { isDetachedSpaceItem } from 'utils/structure';

export function openingRunsFootprints(
  itemId: number | undefined,
  footprints: FootprintPolygonsType | null
): FootprintPolygonsType | null {
  return itemId != null && isDetachedSpaceItem(itemId) ? null : footprints;
}

export function spaceOriginFeet(space: OpeningRunSpaceType): PositionType {
  return geometryOriginFeet(spaceToFeetEdgeRunGeometry(space));
}

export function levelOpenNeighborCandidates(
  space: OpeningRunSpaceType,
  level: LevelType | null | undefined,
  homeOrientation: ExposureDirectionType | undefined
): OpenNeighborSpaceType[] {
  const openToIds = space.openTo ?? [];
  const footprints = getEffectiveFootprintPolygons(level);
  return (level?.sections ?? [])
    .flatMap((section) => section.spaces)
    .filter(
      (candidate) =>
        candidate.uniqueId !== space.uniqueId &&
        openToIds.includes(candidate.uniqueId)
    )
    .map((candidate) => ({
      geometryFeet: spaceToFeetEdgeRunGeometry(candidate),
      footprints: openingRunsFootprints(candidate.itemId, footprints),
      wallEdges: spaceWallEdges(candidate.sizingAttributes, homeOrientation)
    }));
}

export function spaceOpenNeighborRuns(
  space: OpeningRunSpaceType,
  level: LevelType | null | undefined,
  homeOrientation: ExposureDirectionType | undefined
): OpenNeighborRunsType[] {
  return openNeighborOpeningRuns(
    levelOpenNeighborCandidates(space, level, homeOrientation)
  );
}

export function openingRunsForSpace(
  space: OpeningRunSpaceType,
  level: LevelType | null | undefined,
  homeOrientation: ExposureDirectionType | undefined
): EdgeRunType[] {
  const geometryFeet = spaceToFeetEdgeRunGeometry(space);
  const footprints = openingRunsFootprints(
    space.itemId,
    getEffectiveFootprintPolygons(level)
  );

  return openingRunsWithOpenNeighborOverhang(
    getSpaceEdgeRunsFromFeet(geometryFeet, footprints),
    geometryOriginFeet(geometryFeet),
    spaceOpenNeighborRuns(space, level, homeOrientation)
  );
}

export function openingRunBoundsForSpaceEdge(
  space: OpeningRunSpaceType,
  edge: OpeningEdgeType,
  level: LevelType | null | undefined,
  homeOrientation: ExposureDirectionType | undefined
): OpeningRunBoundsType {
  return runBoundsForEdge(
    openingRunsForSpace(space, level, homeOrientation),
    edge
  );
}
